import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import Logo from '@/components/Logo';
import { Loader2, Landmark, CreditCard, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';

const CARD_BRANDS = ['Visa', 'Mastercard', 'American Express', 'Discover'];

export default function BillingSetup() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    bank_name: '',
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    confirm_account_number: '',
    card_on_file: false,
    card_holder_name: '',
    card_brand: '',
    card_last4: '',
    card_expiry: '',
    card_billing_zip: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/billing/public/${token}`);
        if (!cancelled) setTokenInfo(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'This link is invalid or expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const validate = () => {
    if (!form.bank_name.trim()) return 'Bank name is required';
    if (!form.account_holder_name.trim()) return 'Account holder name is required';
    if (!/^\d{9}$/.test(form.routing_number)) return 'Routing number must be exactly 9 digits';
    if (!/^[0-9-\s]{4,20}$/.test(form.account_number)) return 'Account number must be numeric (4–20 digits)';
    if (form.account_number !== form.confirm_account_number) return 'Account numbers do not match';
    if (form.card_on_file) {
      if (!form.card_holder_name.trim()) return 'Cardholder name is required';
      if (!form.card_brand) return 'Card brand is required';
      if (!/^\d{4}$/.test(form.card_last4)) return 'Card last 4 must be 4 digits';
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.card_expiry)) return 'Expiry must be MM/YY';
      if (!/^\d{5}$/.test(form.card_billing_zip)) return 'Billing ZIP must be 5 digits';
    }
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        bank_name: form.bank_name.trim(),
        account_holder_name: form.account_holder_name.trim(),
        routing_number: form.routing_number.trim(),
        account_number: form.account_number.replace(/[\s-]/g, ''),
        card_on_file: form.card_on_file,
        ...(form.card_on_file && {
          card_holder_name: form.card_holder_name.trim(),
          card_brand: form.card_brand,
          card_last4: form.card_last4.trim(),
          card_expiry: form.card_expiry.trim(),
          card_billing_zip: form.card_billing_zip.trim(),
        }),
      };
      await api.post(`/billing/public/${token}`, payload);
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-deep)' }}>
        <Loader2 className="animate-spin" size={32} style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  if (error && !tokenInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-deep)' }}>
        <div className="max-w-md w-full text-center p-8 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <AlertCircle size={40} className="mx-auto mb-4" style={{ color: 'var(--danger)' }} />
          <h1 className="font-['Manrope'] font-bold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Link Unavailable</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
          <p className="text-xs mt-4" style={{ color: 'var(--text-faint)' }}>Please contact your Millennials representative for a new link.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-deep)' }}>
        <div className="max-w-md w-full text-center p-8 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
          <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: 'var(--primary)' }} />
          <h1 className="font-['Manrope'] font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>Billing Info Received</h1>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            Thank you, {tokenInfo?.company_name}. Your billing information has been securely received.
            A Millennials representative will be in touch shortly.
          </p>
          <p className="text-xs" style={{ color: 'var(--text-faint)' }}>You can safely close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-deep)' }}>
      <header className="border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Logo />
          <div className="text-right">
            <p className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>
              Tenant
            </p>
            <p className="text-xs font-bold" style={{ color: 'var(--primary)' }}>
              {tokenInfo?.tenant_group === 'MM' ? 'Millennials Maintenance' :
               tokenInfo?.tenant_group === 'MT' ? 'Millennials Trucking' : '—'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-['Manrope'] font-bold text-3xl mb-2" style={{ color: 'var(--text-primary)' }}>
            Complete Your Billing Setup
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {tokenInfo?.company_name} — secure billing information collection
          </p>
        </div>

        <div className="mb-6 p-4 rounded-lg flex items-start gap-3" style={{ background: 'rgba(246, 194, 50, 0.08)', border: '1px solid rgba(246, 194, 50, 0.25)' }}>
          <ShieldCheck size={18} style={{ color: 'var(--primary)' }} className="mt-0.5 flex-shrink-0" />
          <div className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Your information is encrypted at rest.</strong> We never ask for
            full credit card numbers or security codes. Card info is optional and limited to the last 4 digits.
            Access to your bank account number is restricted and audit-logged.
          </div>
        </div>

        {/* ── Bank / ACH Info ── */}
        <section className="card-slate p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <Landmark size={18} style={{ color: 'var(--primary)' }} />
            <h2 className="font-['Manrope'] font-bold text-base" style={{ color: 'var(--text-primary)' }}>Bank / ACH Information</h2>
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ background: 'var(--primary)', color: '#1A120E' }}>Required</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bank Name *" value={form.bank_name} onChange={v => upd('bank_name', v)} placeholder="e.g. Chase Bank" testid="billing-bank-name" />
            <Field label="Name on Account *" value={form.account_holder_name} onChange={v => upd('account_holder_name', v)} placeholder="As shown on check" testid="billing-account-holder" />
            <Field label="Routing Number * (9 digits)" value={form.routing_number} onChange={v => upd('routing_number', v.replace(/\D/g, '').slice(0, 9))} placeholder="021000021" testid="billing-routing" />
            <div />
            <Field label="Account Number *" value={form.account_number} onChange={v => upd('account_number', v.replace(/[^\d-\s]/g, '').slice(0, 20))} placeholder="Enter account number" type="password" testid="billing-account" />
            <Field label="Confirm Account Number *" value={form.confirm_account_number} onChange={v => upd('confirm_account_number', v.replace(/[^\d-\s]/g, '').slice(0, 20))} placeholder="Re-enter to confirm" type="password" testid="billing-account-confirm" />
          </div>
        </section>

        {/* ── Optional Card on File ── */}
        <section className="card-slate p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <CreditCard size={18} style={{ color: 'var(--text-muted)' }} />
              <h2 className="font-['Manrope'] font-bold text-base" style={{ color: 'var(--text-primary)' }}>Card on File</h2>
              <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>Optional</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="billing-card-toggle">
              <input type="checkbox" checked={form.card_on_file} onChange={e => upd('card_on_file', e.target.checked)} className="w-4 h-4 accent-yellow-500" />
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Add a card</span>
            </label>
          </div>

          {form.card_on_file ? (
            <>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--text-faint)' }}>
                We only collect the last 4 digits, brand, and expiry — never the full number. Ops will contact you when a charge is needed.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Cardholder Name *" value={form.card_holder_name} onChange={v => upd('card_holder_name', v)} testid="billing-card-name" />
                <div>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Card Brand *</label>
                  <select value={form.card_brand} onChange={e => upd('card_brand', e.target.value)} data-testid="billing-card-brand"
                    className="w-full h-11 px-4 rounded-md font-['IBM_Plex_Sans'] text-sm focus:outline-none"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                    <option value="">Select...</option>
                    {CARD_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <Field label="Last 4 Digits *" value={form.card_last4} onChange={v => upd('card_last4', v.replace(/\D/g, '').slice(0, 4))} placeholder="1234" testid="billing-card-last4" />
                <Field label="Expiry MM/YY *" value={form.card_expiry} onChange={v => {
                  let s = v.replace(/\D/g, '').slice(0, 4);
                  if (s.length >= 3) s = s.slice(0, 2) + '/' + s.slice(2);
                  upd('card_expiry', s);
                }} placeholder="08/27" testid="billing-card-expiry" />
                <Field label="Billing ZIP *" value={form.card_billing_zip} onChange={v => upd('card_billing_zip', v.replace(/\D/g, '').slice(0, 5))} placeholder="78704" testid="billing-card-zip" />
              </div>
            </>
          ) : (
            <p className="text-xs italic" style={{ color: 'var(--text-faint)' }}>
              Toggle the switch above to add an optional credit card reference. (You can always add this later.)
            </p>
          )}
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-md flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <AlertCircle size={16} style={{ color: 'var(--danger)' }} className="mt-0.5 flex-shrink-0" />
            <p className="text-xs" style={{ color: 'var(--danger)' }} data-testid="billing-error">{error}</p>
          </div>
        )}

        <button onClick={submit} disabled={submitting} data-testid="billing-submit-btn"
          className="w-full h-12 rounded-md font-['Manrope'] font-bold text-sm transition-all disabled:opacity-50"
          style={{ background: 'var(--primary)', color: '#1A120E' }}>
          {submitting ? (<span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16} /> Submitting...</span>) : 'Submit Billing Information'}
        </button>

        <p className="text-[11px] text-center mt-6" style={{ color: 'var(--text-faint)' }}>
          🔒 This page is encrypted (HTTPS). Your data is encrypted at rest with AES-256.
        </p>
        <p
          className="text-[10px] text-center mt-3"
          style={{ color: 'var(--text-faint)', opacity: 0.6 }}
          data-testid="billing-setup-credit-line"
        >
          Portal designed &amp; built by{' '}
          <a
            href="https://stellardesign.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: 'var(--primary)' }}
          >
            Stellar Design Corp
          </a>
          {' '}· St. Petersburg, FL · Est. 2024
        </p>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder = '', type = 'text', testid }) {
  return (
    <div>
      <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full h-11 px-4 rounded-md font-['IBM_Plex_Sans'] text-sm focus:outline-none transition-colors"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
    </div>
  );
}
